// Pure logic for the Tools & files launcher: which rows to show in which
// section, search filtering, and keyboard-highlight movement. Kept free of
// React/store imports so it is unit-testable.
import { TOOL_LABELS, type PanelTab, type ToolType } from '~/lib/stores/panel-tabs';

/** Launcher rows that DO something instead of opening a tab. */
export type LauncherAction = 'download-code';

export interface LauncherEntry {
  key: string;
  kind: 'existing' | 'suggested' | 'action';
  tabId?: string;
  tool?: ToolType;
  action?: LauncherAction;
  label: string;
  description: string;
  icon: string;
}

const TOOL_META: Record<ToolType, { description: string; icon: string }> = {
  deployment: { description: 'Publish your site to the web', icon: 'i-ph:rocket-launch' },
  analytics: { description: 'Pageviews, visitors, and traffic sources', icon: 'i-ph:chart-line' },
  forms: { description: 'Lead captures and form submissions', icon: 'i-ph:envelope-simple' },
  integrations: { description: 'Add sign-in, payments, and more', icon: 'i-ph:plugs-connected' },
  payments: { description: 'Take payments, refunds, and go live', icon: 'i-ph:credit-card' },
  shop: { description: 'Products, stock, orders, and discounts', icon: 'i-ph:storefront' },
  funnels: { description: 'Sales funnels, offers, and conversion', icon: 'i-ph:funnel' },
  marketing: { description: 'Tracking tags, consent, campaigns, and email', icon: 'i-ph:megaphone' },
  reviews: { description: 'Approve and reply to customer reviews', icon: 'i-ph:star' },
  content: { description: 'Team, menu, FAQ and other lists your site shows', icon: 'i-ph:database' },
  automations: { description: 'When something happens, email, post a webhook, or reward', icon: 'i-ph:lightning' },
  sections: { description: 'Reorder, hide and edit the sections of each page', icon: 'i-ph:stack' },
  marketplace: { description: 'Plugins and themes for your app', icon: 'i-ph:squares-four' },
  domains: { description: 'Connect custom domains to your site', icon: 'i-ph:globe' },
  assets: { description: 'Images and media for your page', icon: 'i-ph:image' },
  code: { description: "View your site's code", icon: 'i-ph:code' },
  'sub-agent': { description: 'Sub-agent activity log', icon: 'i-ph:robot' },
};

const PREVIEW_META = { description: 'Preview your site', icon: 'i-ph:globe-simple' };

// Tools offered in "Suggested" beyond the project's dashboard tools.
const EXTRA_SUGGESTED_TOOLS: ToolType[] = ['code'];

/**
 * Actions, in their own section: they open no tab, so they carry no `tool` and
 * must not be mixed into `suggested` (whose entries are all tabs to open).
 * "Download code" also has an entry point in the Code tab; this is how someone
 * who never opens that tab finds it.
 */
const ACTIONS: readonly LauncherEntry[] = [
  {
    key: 'action-download-code',
    kind: 'action',
    action: 'download-code',
    label: 'Download code',
    description: 'Get the whole project as a zip file',
    icon: 'i-ph:download-simple',
  },
];

function matches(entry: LauncherEntry, query: string): boolean {
  const q = query.trim().toLowerCase();

  if (!q) {
    return true;
  }

  return entry.label.toLowerCase().includes(q) || entry.description.toLowerCase().includes(q);
}

export function buildLauncherSections(
  tabs: PanelTab[],
  availableTools: ToolType[],
  query: string,
): { existing: LauncherEntry[]; suggested: LauncherEntry[]; actions: LauncherEntry[] } {
  const openTabs = tabs.filter((t) => t.type === 'preview' || (t.type === 'tool' && t.tool));

  const existing: LauncherEntry[] = openTabs.map((tab) => {
    const meta = tab.type === 'preview' ? PREVIEW_META : TOOL_META[tab.tool as ToolType];

    return {
      key: `existing-${tab.id}`,
      kind: 'existing',
      tabId: tab.id,
      tool: tab.tool ?? undefined,
      label: tab.label,
      description: meta.description,
      icon: meta.icon,
    };
  });

  const openTools = new Set(openTabs.map((t) => t.tool).filter(Boolean));

  const suggested: LauncherEntry[] = [...availableTools, ...EXTRA_SUGGESTED_TOOLS]
    .filter((tool) => !openTools.has(tool))
    .map((tool) => ({
      key: `suggested-${tool}`,
      kind: 'suggested' as const,
      tool,
      label: TOOL_LABELS[tool],
      description: TOOL_META[tool].description,
      icon: TOOL_META[tool].icon,
    }));

  return {
    existing: existing.filter((e) => matches(e, query)),
    suggested: suggested.filter((e) => matches(e, query)),
    actions: ACTIONS.filter((e) => matches(e, query)),
  };
}

// Wrap-around keyboard highlight. -1 means "nothing highlighted".
export function moveHighlight(total: number, current: number, delta: 1 | -1): number {
  if (total === 0) {
    return -1;
  }

  return (current + delta + total) % total;
}
